import { useTranslation } from "react-i18next";
import { getGenderIcon } from "@/hooks/use-gender";
import { cn } from "@/lib/utils";

type SelectedTherapist = {
	name?: string | null;
	registrationNumber?: string | null;
	employmentType?: string | null;
	gender?: string | null;
};

function initials(name?: string | null): string {
	return (name ?? "")
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((p) => p[0]?.toUpperCase() ?? "")
		.join("");
}

/**
 * The "selected therapist" confirmation card for the legacy "cari terapis"
 * flow. Uses the unified Option-D layout (left primary rail + square avatar +
 * "Change" link) so it matches the kalender picker's committed-slot card.
 */
export function SelectedTherapistCard({
	therapist,
	onUnselect,
	className,
}: {
	therapist: SelectedTherapist;
	onUnselect: () => void;
	className?: string;
}) {
	const { t } = useTranslation("appointments-form", {
		keyPrefix: "appt_schedule.schedule_picker.common",
	});
	const { t: tForm } = useTranslation("appointments-form");

	return (
		<div
			className={cn(
				"flex items-center gap-3 rounded-lg border border-border border-l-[3px] border-l-primary bg-card px-3 py-2.5 shadow-sm",
				className,
			)}
		>
			<div className="grid size-10 shrink-0 place-items-center rounded-xl border border-border bg-accent font-semibold text-accent-foreground text-sm">
				{initials(therapist.name)}
			</div>

			<div className="min-w-0 flex-1">
				<p
					title={therapist.name ?? undefined}
					className="truncate font-bold text-sm uppercase tracking-wide"
				>
					{therapist.name}
				</p>
				<p className="truncate text-muted-foreground text-xs uppercase">
					{therapist.registrationNumber && (
						<>#{therapist.registrationNumber} · </>
					)}
					{therapist.employmentType
						? tForm(
								`common.employment_types.${therapist.employmentType}`,
								therapist.employmentType,
							)
						: "N/A"}
					{therapist.gender && (
						<>
							{" · "}
							<span className="inline-flex items-center align-middle">
								{getGenderIcon(
									therapist.gender,
									"inline size-3 opacity-75 mr-0.5 align-middle",
								)}
								{tForm(
									`appt_schedule.fields.pref_therapist_gender.options.${therapist.gender.toLowerCase()}`,
									therapist.gender,
								)}
							</span>
						</>
					)}
				</p>
			</div>

			<button
				type="button"
				onClick={onUnselect}
				title={t("change")}
				className="shrink-0 font-medium text-primary text-xs hover:underline"
			>
				{t("change")}
			</button>
		</div>
	);
}
